import { Dayjs } from 'dayjs'

import { AstraMultiSelect, AstraPopover, AstraTooltip, getOnlyDate } from '~/astra'
import { createPractitionerScheduleStatus, deleteBatchPractitionerScheduleStatus, deletePractitionerScheduleStatus } from '~/store/di-entity.api.generated'
import { StatusCode } from '~/store/slices/filterSlice'
import { useStore } from '~/store/store'
import { selectPractitionerScheduleStatus, selectPractitionerStatusDefinition, useActivePractitioners, usePractitionerScheduleStatuses } from '~/storm'
import { getPractitionerFullname } from '~/utils/dips'

import { PractitionersDisplay } from './PractitionersDisplay'

type Props = {
    label: string
    date: Dayjs
    status: StatusCode
}

export const PractitionersSelect = ({ label, date, status }: Props) => {
    const activePractitioners = useActivePractitioners()

    const statuses = usePractitionerScheduleStatuses({
        where: {
            scheduled_date: getOnlyDate(date),
            status_code: status,
            practitioner_id: { in: activePractitioners.map(p => p.id) },
        },
        include: { practitioner: { dipsResource: true } },
        orderBy: ['created_at'],
    })

    const values = statuses.map(status => status.practitioner_id)
    const options = activePractitioners.map(({ short_name, id }) => ({ label: short_name, value: id }))

    return (
        <AstraPopover
            className="min-w-[290px]"
            align="start"
            side="right"
            noPadding
            popoverContent={
                <div className="flex max-w-[280px] select-text flex-col text-left">
                    <p className="border-b px-2 py-1 text-base font-normal text-gray-400">{label}</p>
                    <div className="max-h-72 p-2 text-sm">
                        <AstraMultiSelect
                            values={values}
                            options={options}
                            placeholder="Velg..."
                            onActionMeta={meta => {
                                const state = useStore.getState()
                                const dateOnly = getOnlyDate(date)
                                const getStatusId = (practitionerId: number): number | undefined =>
                                    selectPractitionerScheduleStatus(state, {
                                        where: {
                                            scheduled_date: dateOnly,
                                            status_code: status,
                                            practitioner_id: practitionerId,
                                        },
                                    })?.id

                                if (meta.action === 'clear') {
                                    const statusesIdToDelete = meta.removedValues
                                        .map(({ value }) => getStatusId(value))
                                        .filter((id): id is number => id !== undefined)
                                    if (statusesIdToDelete.length > 0) {
                                        void deleteBatchPractitionerScheduleStatus(statusesIdToDelete)
                                    }
                                } else if (meta.action === 'select-option') {
                                    const practitionerId = meta.option?.value
                                    if (practitionerId === undefined) return

                                    const definition = selectPractitionerStatusDefinition(state, { where: { code: status } })
                                    if (!definition?.uuid) return

                                    void createPractitionerScheduleStatus({
                                        practitioner_id: practitionerId,
                                        scheduled_date: dateOnly,
                                        status_code: status,
                                        practitioner_status_definition_uuid: definition.uuid,
                                    })
                                } else if (meta.action === 'pop-value' || meta.action === 'remove-value') {
                                    const statusIdToDelete = getStatusId(meta.removedValue.value)
                                    if (statusIdToDelete !== undefined) {
                                        void deletePractitionerScheduleStatus(statusIdToDelete)
                                    }
                                } else if (meta.action === 'deselect-option') {
                                    const practitionerId = meta.option?.value
                                    if (practitionerId === undefined) return
                                    const statusIdToDelete = getStatusId(practitionerId)
                                    if (statusIdToDelete !== undefined) {
                                        void deletePractitionerScheduleStatus(statusIdToDelete)
                                    }
                                }
                            }}
                        />
                    </div>
                </div>
            }
        >
            <div className="h-full">
                <AstraTooltip tooltipContent={statuses.map(status => getPractitionerFullname(status.practitioner)).join(', ')}>
                    <div className="flex h-full cursor-pointer flex-nowrap items-center justify-center gap-x-2 overflow-hidden transition-all hover:bg-gray-100">
                        <PractitionersDisplay names={statuses.map(status => status.practitioner?.short_name ?? '')} />
                    </div>
                </AstraTooltip>
            </div>
        </AstraPopover>
    )
}
